import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2, ArrowLeft, BookOpen, Search, RefreshCw,
  Lock, Unlock, CalendarDays, GraduationCap,
} from "lucide-react";
import { useChiefGetExamsQuery } from "../../api/chiefExaminerApi";

const STAGE_LABELS: Record<string, string> = {
  none:   "No requirement",
  stage1: "Stage 1",
  stage2: "Stage 2",
};

export default function ChiefExamsPage() {
  const { data: exams = [], isLoading, refetch, isFetching } = useChiefGetExamsQuery();
  const [q, setQ] = useState("");

  const filtered = q.trim()
    ? exams.filter((e: any) =>
        e.title?.toLowerCase().includes(q.toLowerCase()) ||
        e.code?.toLowerCase().includes(q.toLowerCase())
      )
    : exams;

  const activeCount   = exams.filter((e: any) => e.is_active).length;
  const inactiveCount = exams.length - activeCount;

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-4 md:p-6 pb-20">

      {/* ── Stats row ──────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-100 p-5 rounded-3xl shadow-sm flex items-center gap-4">
          <div className="w-11 h-11 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
            <BookOpen className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">Total Exams</div>
            <div className="text-xl font-black text-gray-900">{isLoading ? "…" : exams.length}</div>
          </div>
        </div>

        <div className="bg-white border border-gray-100 p-5 rounded-3xl shadow-sm flex items-center gap-4">
          <div className="w-11 h-11 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
            <Unlock className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">Active</div>
            <div className="text-xl font-black text-emerald-700">{isLoading ? "…" : activeCount}</div>
          </div>
        </div>

        <div className="bg-white border border-gray-100 p-5 rounded-3xl shadow-sm flex items-center gap-4">
          <div className="w-11 h-11 rounded-2xl bg-gray-50 text-gray-400 flex items-center justify-center shrink-0">
            <Lock className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">Inactive</div>
            <div className="text-xl font-black text-gray-500">{isLoading ? "…" : inactiveCount}</div>
          </div>
        </div>
      </div>

      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <Link to="/exams/chief"
              className="p-3 rounded-xl border bg-white hover:bg-gray-50 transition-all shadow-sm">
              <ArrowLeft className="w-4 h-4 text-gray-500" />
            </Link>
            <div className="w-14 h-14 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-xl shadow-gray-200">
              <BookOpen className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-gray-900 tracking-tight leading-none uppercase">
                All Exams
              </h1>
              <p className="text-gray-400 text-sm mt-1.5 font-medium">
                Examination registry — read-only view
              </p>
            </div>
          </div>

          <button
            onClick={() => refetch()}
            className="p-2.5 border rounded-xl hover:bg-gray-50 transition-all shadow-sm"
          >
            <RefreshCw className={`w-4 h-4 text-gray-500 ${isFetching ? "animate-spin" : ""}`} />
          </button>
        </div>

        {/* Search */}
        <div className="mt-6 relative">
          <Search className="w-4 h-4 text-gray-300 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by title or code…"
            className="w-full pl-10 pr-4 py-3 border border-gray-100 rounded-2xl text-sm font-bold text-gray-800 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-50 transition-all bg-gray-50/50 placeholder:text-gray-300 placeholder:font-normal"
          />
        </div>
      </div>

      {/* ── Exam list ──────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-100 rounded-[2.5rem] shadow-sm overflow-hidden">
        <div className="px-8 py-5 border-b border-gray-50 flex items-center justify-between">
          <div className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
            Examination Registry
          </div>
          <span className="text-[10px] font-black text-gray-400">
            {filtered.length} {filtered.length === 1 ? "exam" : "exams"}
          </span>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-6 h-6 animate-spin text-gray-200" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-24 text-center">
            <BookOpen className="w-10 h-10 mx-auto text-gray-100 mb-3" />
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-300">
              {q ? `No exams match "${q}"` : "No exams found"}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {filtered.map((exam: any) => (
              <Link key={exam.id} to={`/exams/chief/exams/${exam.id}`}
                className="flex items-start gap-5 px-8 py-5 hover:bg-gray-50/80 transition-all group">

                {/* Status dot */}
                <div className={`w-2.5 h-2.5 rounded-full mt-2 shrink-0 ${
                  exam.is_active ? "bg-emerald-400" : "bg-gray-200"
                }`} />

                {/* Main info */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    {/* Code badge */}
                    <span className="inline-block px-2 py-0.5 rounded-lg bg-gray-900 text-white text-[9px] font-black uppercase tracking-widest">
                      {exam.code}
                    </span>
                    {/* Status badge */}
                    <span className={`inline-block px-2 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-widest border ${
                      exam.is_active
                        ? "bg-emerald-50 border-emerald-100 text-emerald-600"
                        : "bg-gray-50 border-gray-200 text-gray-400"
                    }`}>
                      {exam.is_active ? "Active" : "Inactive"}
                    </span>
                  </div>

                  <p className="font-black text-gray-900 uppercase tracking-tight text-sm leading-snug">
                    {exam.title}
                  </p>

                  {/* Meta row */}
                  <div className="flex flex-wrap items-center gap-4 mt-2">
                    {/* Courses */}
                    <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                      <BookOpen className="w-3 h-3" />
                      {exam.courses_count} active course{exam.courses_count !== 1 ? "s" : ""}
                      {exam.total_courses !== exam.courses_count && (
                        <span className="text-gray-300">/ {exam.total_courses} total</span>
                      )}
                    </span>

                    {/* Diets */}
                    <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                      <CalendarDays className="w-3 h-3" />
                      {exam.diets_count} diet{exam.diets_count !== 1 ? "s" : ""}
                    </span>

                    {/* Logbook requirement */}
                    {exam.requires_logbook_stage && exam.requires_logbook_stage !== "none" && (
                      <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-amber-500 uppercase tracking-wider">
                        <GraduationCap className="w-3 h-3" />
                        {STAGE_LABELS[exam.requires_logbook_stage] ?? exam.requires_logbook_stage}
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}
